import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Parse a value that MUST be a whole number (KM odômetro, dias, quantidade).
 * BR heuristic: dot and comma are treated as thousand separators (dropped).
 * "10.456" → 10456   "10,456" → 10456   "10456" → 10456
 * If the user typed decimals like "10.5", the .5 is dropped (only integer part).
 */
export function parseInt10(v) {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return isNaN(v) ? 0 : Math.trunc(v);
  const cleaned = String(v).replace(/[^\d\-]/g, "").trim();
  if (!cleaned || cleaned === "-") return 0;
  const n = parseInt(cleaned, 10);
  return isNaN(n) ? 0 : n;
}

/**
 * Parse a numeric input accepting both comma (BR) and dot (US) as decimal separator.
 * Rules:
 *   • "1.500,50" (BR full)   → dot = thousands, comma = decimal → 1500.5
 *   • "1,500.50" (US full)   → comma = thousands, dot = decimal → 1500.5
 *   • "38,5" or "1500,50"    → comma = decimal (drop dots) → 38.5 / 1500.5
 *   • "37.864" (only dot)    → dot = decimal → 37.864 (NOT 37864)
 *   • "1500" (plain digits)  → 1500
 * This is aligned with the natural-typing MoneyInput, where users type freely
 * and only USE comma for decimals (BR standard). If a user really wants 1500
 * with a thousand separator they should type it with the decimal comma
 * ("1.500,00") or plain ("1500").
 * Empty/invalid values return 0.
 */
export function parseNum(v) {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return isNaN(v) ? 0 : v;
  let cleaned = String(v).replace(/[^\d,.\-]/g, "").trim();
  if (!cleaned || cleaned === "-" || cleaned === "," || cleaned === ".") return 0;
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  const dotCount = (cleaned.match(/\./g) || []).length;
  if (lastComma > -1 && lastDot > -1) {
    if (lastComma > lastDot) {
      // BR: 1.500,50 → drop dots, comma becomes decimal
      cleaned = cleaned.replace(/\./g, "").replace(",", ".");
    } else {
      // US: 1,500.50 → drop commas
      cleaned = cleaned.replace(/,/g, "");
    }
  } else if (lastComma > -1) {
    // Only comma → BR decimal (drop stray dots, comma → decimal)
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (dotCount > 1) {
    // Multiple dots, no comma → BR thousand separators (5.800.000 = 5800000)
    cleaned = cleaned.replace(/\./g, "");
  }
  // Single dot or plain digits → treat dot as decimal (natural JS)
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

/**
 * Format a number in BR locale (e.g. 1500.5 → "1.500,5", 37864 → "37.864").
 * Non-monetary display helper for tons, KM, liters.
 * Empty/invalid values render as "0".
 */
export function fmtN(n, maxFrac = 3) {
  if (n === null || n === undefined || n === "" || isNaN(Number(n))) return "0";
  return Number(n).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: maxFrac });
}

/**
 * Format an ISO date string (YYYY-MM-DD or full ISO) to Brazilian format DD/MM/YYYY.
 * Empty or invalid values return "-".
 */
export function fmtDate(iso) {
  if (!iso) return "-";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

/**
 * Format an ISO datetime to DD/MM/YYYY HH:mm.
 */
export function fmtDateTime(iso) {
  if (!iso) return "-";
  try {
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return "-";
    return dt.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return "-"; }
}
